import Vue from 'vue';
import Router from 'vue-router';

import Full from '@/Full';

import Cockpit from '@/components/Dashboard-APP_TARGET';
import CheckExplorer from '@/components/Survey/CheckExplorer';
import MediaExplorer from '@/components/Survey/MediaExplorer';
import RawData from '@/components/Survey/RawData';
import Frequencies from '@/components/Survey/Frequencies';
import AggregatedReports from '@/components/Survey/AggregatedReports';

import Administration from '@/components/Administration/Administration';
import ClientSettings from '@/components/Administration/ClientSettings';
import SurveySettings from '@/components/Administration/SurveySettings';
import GeneralSettings from '@/components/Administration/GeneralSettings';

import UserSettings from '@/components/User/UserSettings';
import UserProfile from '@/components/User/UserProfile';
import NotificationSettings from '@/components/User/NotificationSettings';
import Download from '@/components/Download';

import Imprint from '@/components/Imprint';
import DataProtection from '@/components/DataProtection';
import Page401 from '@/components/Page401';
import Page404 from '@/components/Page404';
import Page500 from '@/components/Page500';
import PageSignInFailed from '@/components/PageSignInFailed';
import Callback from '@/components/Callback';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import Comparison from '@/components/Comparison-APP_TARGET';

import AuthenticationService from '@/services/AuthenticationService';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'active',

  routes: [
    {
      path: '/',
      name: 'Home',
      meta: { MessageKey: 'Webportal.Home', requiresAuth: true },
      redirect: AppConfig.methods.hasDashboard() ? '/cockpit' : '/survey/checkexplorer',
      component: Full,
      children: [
        {
          path: 'cockpit',
          name: 'Cockpit',
          meta: { MessageKey: 'Webportal.Dashboard' },
          component: Cockpit
        },
        {
          path: 'comparison',
          name: 'Comparison',
          meta: { MessageKey: 'Webportal.DashboardBMW.FocusKPI' },
          component: Comparison
        },
        {
          path: 'survey',
          name: 'Survey',
          meta: { MessageKey: 'Webportal.Survey' },
          redirect: '/survey/checkexplorer',
          component: {
            render(c) { return c('router-view'); }
          },
          children: [
            {
              path: 'checkexplorer',
              name: 'Check explorer',
              meta: { MessageKey: 'Webportal.CheckExplorer' },
              component: CheckExplorer
            },

            {
              path: 'mediaexplorer',
              name: 'Media explorer',
              meta: { MessageKey: 'Webportal.MediaExplorer' },
              component: MediaExplorer
            },

            {
              path: 'rawdata',
              name: 'Raw data',
              meta: { MessageKey: 'Webportal.RawData' },
              component: RawData
            },

            {
              path: 'frequencies',
              name: 'Frequencies',
              meta: { MessageKey: 'Webportal.Frequencies' },
              component: Frequencies
            },

            {
              path: 'aggregatedreports',
              name: 'Aggregated Reports',
              meta: { MessageKey: 'Webportal.AggregatedReports' },
              component: AggregatedReports
            }
          ]
        },
        {
          path: 'administration',
          name: 'Administration',
          meta: { MessageKey: 'Webportal.Administration' },
          redirect: '/administration/clientsettings',
          component: Administration,
          children: [
            {
              path: 'clientsettings',
              name: 'Client settings',
              component: ClientSettings
            },
            {
              path: 'surveysettings',
              name: 'Survey settings',
              component: SurveySettings
            },
            {
              path: 'generalsettings',
              name: 'General settings',
              component: GeneralSettings
            }
          ]
        },
        {
          path: 'user',
          name: 'User',
          meta: { MessageKey: 'Webportal.User' },
          redirect: '/user/profile',
          component: UserSettings,
          children: [
            {
              path: 'profile',
              name: 'Profile',
              component: UserProfile
            },
            {
              path: 'notificationsettings',
              name: 'Notification settings',
              component: NotificationSettings
            }
          ]
        },
        {
          path: 'imprint',
          name: 'Imprint',
          meta: { MessageKey: 'Webportal.Imprint' },
          component: Imprint
        },
        {
          path: 'dataprotection',
          name: 'DataProtection',
          meta: { MessageKey: 'Webportal.DataProtection' },
          component: DataProtection
        }
      ]
    },

    {
      path: '/callback',
      component: {
        render(c) {
          // Check if already logged in. This was introduced because when pressing F5 after successful login in Firefox,
          // the callback URL was loaded again but resulted in an error "No matching state found in storage".
          AuthenticationService.getUser().then(() => {
            // If not, then continue callback workflow
            if (!AuthenticationService.isAuthenticated)
              return AuthenticationService.callback();
          }).then(() => {
            // App needs to be restarted (instead of $router.replace) to allow custom theme which is only evaluated once on startup
            location.replace('/');
          }).catch((e) => {
            if (e === 'unauthorized') {
              // Logout user after 5 seconds
              setTimeout(() => { AuthenticationService.logout(); }, 5000);

              this.$router.push({ name: 'unauthorized'});
            }
            else
              this.$router.push({ name: 'signinfailed'});
          });

          return c(Callback);
        }
      },
    },

    // HACK: AutoLogin SSO
    {
      path: '/isg',
      component: {
        render(c) {
          if (AuthenticationService.isAuthenticated) {
            location.replace('/');
          } else {
            let login_hint = this.$route.query.login_hint;
            if (login_hint) {
              AuthenticationService.login(login_hint).then(() => {
                if (AuthenticationService.isAuthenticated)
                  location.replace('/');
              });
            } else {
              this.$router.push({ name: 'error'});
            }
          }
        }
      },
    },

    {
      path: '/download/:token',
      component: Download
    },

    // ... the default route, when none of the above matches:
    {
      path: '*',
      component: Page404
    },

    {
      path: '*',
      name: 'error',
      component: Page500
    },

    {
      path: '*',
      name: 'unauthorized',
      component: Page401
    },

    {
      path: '*',
      name: 'signinfailed',
      component: PageSignInFailed
    },
  ]
});

// Check for authentication before navigating to target
router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!AuthenticationService.isAuthenticated) {
      AuthenticationService.login().then(() => {
        if (AuthenticationService.isAuthenticated)
          next();
      }).catch((e) => {
        if (e === 'unauthorized') {
          // Logout user after 5 seconds
          setTimeout(() => { AuthenticationService.logout(); }, 5000);

          router.push({ name: 'unauthorized'});
        }
        else
          router.push({ name: 'signinfailed'});
      });
    }
    else {
      next();
    }
  }
  else {
    next();
  }
});

export default router;
